import _extends from '@babel/runtime-corejs3/helpers/extends';
import _defineProperty from '@babel/runtime-corejs3/helpers/defineProperty';
import _slicedToArray from '@babel/runtime-corejs3/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime-corejs3/helpers/objectWithoutProperties';
import React__default, { forwardRef, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './c85c12f1ba24082e26e2c690e510f249c32f5cf0bda075341acc684d55286a9a.cfe-internal.js';
import '../../utils/2d829def7915df1f35176a057ded5c77071616520fe7b6b8ef66f5eb29ca849f.cfe-internal.js';
import '../../hooks/8930fff059088aae845742b95b03a9c78f1349f3c58d1781f854d37c28181edd.cfe-internal.js';
import useIsFocused from '../../hooks/useIsFocused/0f6a89ca013aa6d346e3684cab5faf0ce2a324a0a3a0db4f0f1199945869f4bd.cfe-internal.js';
import useEventCallback from '../../hooks/useEventCallback/0ebce52fac2aa94b4cad29a85740b8586bddbccb9acd7c8786ebbfef7b665ae0.cfe-internal.js';
import { useDidUpdate } from '../../hooks/useDidUpdate/f8ce5c85f293b5c4ca8be6bc5ac9f246003ac61cc91ab03b8814ccaab638a21a.cfe-internal.js';
import useEventListener from '../../hooks/useEventListener/d298373edbbbe27e03edc667e68a196078d5abecbf27f9b61af0b7bc7dd7b6a0.cfe-internal.js';
import mergeRefs from '../../utils/mergeRefs/836dcb860bec00d9c59bd64df8df6df42430840bc5bfc21b6000935baefbeacd.cfe-internal.js';

var _excluded = ["className", "defaultValue", "onChange", "onBlur", "onFocus", "onKeyDown", "onMouseDown", "tabIndex"];
var InlineInput = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    defaultValue = _ref.defaultValue,
    onChange = _ref.onChange,
    onBlur = _ref.onBlur,
    onFocus = _ref.onFocus,
    onKeyDown = _ref.onKeyDown,
    onMouseDown = _ref.onMouseDown,
    _ref$tabIndex = _ref.tabIndex,
    tabIndex = _ref$tabIndex === void 0 ? 0 : _ref$tabIndex,
    rest = _objectWithoutProperties(_ref, _excluded);
  var internalRef = useRef(null);
  var _useState = useState(defaultValue),
    _useState2 = _slicedToArray(_useState, 2),
    lastVal = _useState2[0],
    setLastVal = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isEditing = _useState4[0],
    setIsEditing = _useState4[1];
  var _useIsFocused = useIsFocused(),
    _useIsFocused2 = _slicedToArray(_useIsFocused, 2),
    focusRef = _useIsFocused2[0],
    isFocused = _useIsFocused2[1];
  var handleChange = useEventCallback(function (e) {
    var newVal = e.target.innerText;
    if (newVal !== lastVal) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore: Property 'value' does not exist on type 'HTMLSpanElement'
      e.target.value = newVal;
      onChange === null || onChange === void 0 || onChange(e);
      setLastVal(newVal);
    }
  });
  var handleBlur = function handleBlur(e) {
    setIsEditing(false);
    e.target.value = e.target.innerText;
    onBlur === null || onBlur === void 0 || onBlur(e);
  };
  var handleFocus = function handleFocus(e) {
    setIsEditing(true);
    e.target.value = e.target.innerText;
    onFocus === null || onFocus === void 0 || onFocus(e);
  };
  var handleKeyDown = function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission
      setIsEditing(function (v) {
        return !v;
      });
    } else if (e.key === 'Escape' && isEditing) {
      e.preventDefault(); // Prevent clearing the text in some browsers
      e.stopPropagation(); // prevent event from closing modals or popovers
      setIsEditing(false);
    }
    onKeyDown === null || onKeyDown === void 0 || onKeyDown(e);
  };
  var handleMouseDown = function handleMouseDown(e) {
    if (isFocused) {
      setIsEditing(true);
    }
    onMouseDown === null || onMouseDown === void 0 || onMouseDown(e);
  };
  var handlePaste = function handlePaste(e) {
    // Strip formatting and newlines from pasted data
    if (typeof window !== 'undefined') {
      e.preventDefault(); // prevent paste and manually insert text after stripping formatting
      var text = e.clipboardData.getData('text').replace(/[\n\r]/g, ' ');
      var selection = window.getSelection();
      var range = selection === null || selection === void 0 ? void 0 : selection.getRangeAt(0);
      if (range && selection) {
        range.deleteContents();
        var textNode = document.createTextNode(text);
        range.insertNode(textNode);
        range.selectNodeContents(textNode);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
        handleChange(e);
      }
    }
  };
  useDidUpdate(function () {
    if (typeof window !== 'undefined') {
      if (isEditing && isFocused && internalRef.current) {
        var _window$getSelection;
        (_window$getSelection = window.getSelection()) === null || _window$getSelection === void 0 || _window$getSelection.selectAllChildren(internalRef.current);
      } else {
        var _window$getSelection2;
        (_window$getSelection2 = window.getSelection()) === null || _window$getSelection2 === void 0 || _window$getSelection2.removeAllRanges();
      }
    }
  }, [isEditing, isFocused]);

  // The event listener only triggers the change handler in IE 11. In IE 11, `onInput` does not trigger the change handler.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: `textinput` is a non-standard IE only event so TS doesn't recognize it
  useEventListener(internalRef, 'textinput', handleChange);
  return /*#__PURE__*/React__default.createElement("span", _extends({}, rest, {
    ref: mergeRefs(ref, internalRef, focusRef),
    className: classNames(className, styles.inlineInput, _defineProperty({}, styles.isEditing, isEditing)),
    contentEditable: !isFocused || isEditing,
    suppressContentEditableWarning: !isFocused || isEditing,
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
    onMouseDown: handleMouseDown,
    onInput: handleChange,
    onFocus: handleFocus,
    onPaste: handlePaste,
    role: "textbox",
    "aria-multiline": "false",
    "aria-readonly": isFocused && !isEditing,
    tabIndex: tabIndex
  }), defaultValue);
});

export { InlineInput as default };
